'use client';

import ControlBar from '@/app/[locale]/(worlds)/[roomId]/components/ControlBar';
import EnterFullscreenModal from '@/app/[locale]/(worlds)/[roomId]/components/EnterFullscreenModal';
import RoomAlertOverlay from '@/app/[locale]/(worlds)/[roomId]/components/RoomAlertOverlay';
import { VideoProcessingOverlay } from '@/app/[locale]/(worlds)/[roomId]/components/VideoProcessingStatus';
import ViverseLogoOverlay from '@/app/[locale]/(worlds)/[roomId]/components/ViverseLogoOverlay';
import MoreVideosPanel from '@/components/MoreVideosPanel';
import SharePanel from '@/components/SharePanel';
import { SEARCH_PARAM_KEYS } from '@/constants/auth';
import { BI_CATEGORY_BEHAVIOR_IN_ROOM, BI_CATEGORY_USER_ACTIVITY, BI_CATEGORY_VIDEO_ACTIVITY, BI_EVENT_VIDEO_END, BI_EVENT_VIDEO_PAUSE, BI_EVENT_VIDEO_RESUME, BI_EVENT_VIDEO_WATCH, BI_EVENT_VIEW_COUNT, BI_EVENT_WORLD_ENGAGEMENT } from '@/constants/bi-sdk';
import { IFRAME_SEARCH_PARAM_KEYS, isInternalMessage } from '@/constants/iframe';
import { useAuthContext } from '@/context/AuthContext';
import { useBISdkContext } from '@/context/BISdkContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useCrossPlatformGatingContext } from '@/context/CrossPlatformGatingContext';
import { useMoreVideosPanelContext } from '@/context/MoreVideosPanelContext';
import { useUAContext } from '@/context/UAContext';
import { useVastAdState } from '@/context/VastAdStateContext';
import { useVideoCommentsPanelContext } from '@/context/VideoCommentsPanelContext';
import { useVideoPlaybackContext } from '@/context/VideoPlaybackContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useAdSDK } from '@/hooks/useAdSDK';
import { useAutoFullscreen } from '@/hooks/useAutoFullscreen';
import { IframeHandlers } from '@/hooks/useIframeScrollLock';
import { useIframeSrcTeardown } from '@/hooks/useIframeSrcTeardown';
import { getWorldsDomain } from '@/lib/configs';
import { enterFullscreen, exitFullscreen } from '@/lib/fullscreen';
import { buildStandaloneIframeUrl, getStandaloneIframeAllowPermissions, getStandaloneIframeSandboxPermissions } from '@/lib/iframe';
import { matchOrigin } from '@/lib/postmessage-origins';
import { env } from '@/lib/runtime-env';
import { buildShareUrl } from '@/lib/share-link';
import { addWorldViewCount, completeUserTask, getPostMessageOriginsByRoomId } from '@/services/cms';
import { getSgToken } from '@/services/sg-token';
import { usePostHog } from '@posthog/next';
import { useSearchParams } from 'next/navigation';
import { RefObject, SyntheticEvent, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useEventListener, useTimeout } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';
const COMMENT_DOCK_MIN_HEIGHT = 320;

/**
 * Forward video-specific URL params (?t / ?autoplay / ?controls) and the
 * ?force_landscape_ios flag from the page URL to the BDV iframe URL.
 */
function appendVideoParams(url: string, t: string | null, autoplay: string | null, controls: string | null, forceLandscapeIos: string | null): string {
  if (!t && autoplay === null && controls === null && !forceLandscapeIos) return url;
  try {
    const u = new URL(url);
    if (t) u.searchParams.set('t', t);
    if (autoplay !== null) u.searchParams.set('autoplay', autoplay);
    if (controls !== null) u.searchParams.set('controls', controls);
    if (forceLandscapeIos) {
      u.searchParams.set(IFRAME_SEARCH_PARAM_KEYS.FORCE_LANDSCAPE_IOS, forceLandscapeIos);
    }
    return u.toString();
  } catch {
    return url;
  }
}
const VIVERSE_SDK_METHODS = {
  CHECK_AUTH: 'VIVERSE_SDK/checkAuth',
  LOGIN: 'VIVERSE_SDK/login',
  LOGOUT: 'VIVERSE_SDK/logout'
} as const;
const VIVERSE_BRIDGE_METHODS = {
  C_LOADED: 'VIVERSE_BRIDGE/cLoaded',
  C_LOADED_ACK: 'VIVERSE_BRIDGE/cLoaded:ack',
  C_LCP: 'VIVERSE_BRIDGE/cLCP',
  C_RESOURCES_LOADED: 'VIVERSE_BRIDGE/cResourcesLoaded',
  C_HEARTBEATING: 'VIVERSE_BRIDGE/cHeartbeating',
  C_HEARTBEAT_PAUSE: 'VIVERSE_BRIDGE/cHeartbeatPause',
  C_HEARTBEAT_RESUME: 'VIVERSE_BRIDGE/cHeartbeatResume',
  C_UNLOAD: 'VIVERSE_BRIDGE/cUnload',
  C_LANDSCAPE_ROTATION: 'VIVERSE_BRIDGE/cLandscapeRotation',
  NO_INTENT: 'VIVERSE_BRIDGE'
} as const;
const VIVERSE_APP_METHODS = {
  REQUEST_ADD_POINTS: 'VIVERSE_APP/requestAddPoints',
  REQUEST_ADD_POINTS_ACK: 'VIVERSE_APP/requestAddPoints:ack'
} as const;
const VIDEO_EVENT_TYPES = {
  VIDEO_STARTED: 'VIDEO_STARTED',
  VIDEO_MILESTONE: 'VIDEO_MILESTONE',
  VIDEO_PAUSED: 'VIDEO_PAUSED',
  VIDEO_RESUMED: 'VIDEO_RESUMED',
  VIDEO_ENDED: 'VIDEO_ENDED'
} as const;
const VIVERSE_VIDEO_METHODS = {
  LIKE_CLICK: 'VIVERSE_VIDEO/likeClick',
  SAVE_CLICK: 'VIVERSE_VIDEO/saveClick',
  SHARE_CLICK: 'VIVERSE_VIDEO/shareClick',
  PROFILE_CLICK: 'VIVERSE_VIDEO/profileClick',
  TITLE_CLICK: 'VIVERSE_VIDEO/titleClick',
  COMMENT_CLICK: 'VIVERSE_VIDEO/commentClick',
  MORE_VIDEOS_PANEL_REQUEST: 'VIVERSE_VIDEO/moreVideosPanelRequest',
  CONFIG_READY: 'VIVERSE_VIDEO/configReady',
  CONFIG_INIT: 'VIVERSE_VIDEO/configInit',
  STATE_PATCH: 'VIVERSE_VIDEO/statePatch',
  // Fullscreen coordination: child requests, parent confirms.
  FULLSCREEN_REQUEST: 'VIVERSE_VIDEO/fullscreenRequest',
  FULLSCREEN_STATE_CHANGED: 'VIVERSE_VIDEO/fullscreenStateChanged',
  CURRENT_TIME_REQUEST: 'VIVERSE_VIDEO/currentTimeRequest',
  CURRENT_TIME_RESPONSE: 'VIVERSE_VIDEO/currentTimeResponse',
  SEEK_TO: 'VIVERSE_VIDEO/seekTo',
  AD_STATE_CHANGE: 'VIVERSE_VIDEO/adStateChange'
} as const;
const isValidChildOrigin = (origin: string) => {
  const ALLOWED_ORIGINS = (env('NEXT_PUBLIC_IFRAME_POSTMESSAGE_ORIGINS') || '').split(',').map(o => o.trim()).filter(Boolean);
  return matchOrigin(origin, ALLOWED_ORIGINS);
};
type StandaloneIframeProps = {
  handlers: IframeHandlers;
  ref: RefObject<HTMLIFrameElement | null>;
  isVideoWorld: boolean;
};
function StandaloneIframe({
  handlers,
  ref,
  isVideoWorld
}: StandaloneIframeProps) {
  const {
    deviceType,
    isMobile,
    isMobileLandscape
  } = useUAContext();
  const isMobileDevice = deviceType === 'mobile';
  const {
    shouldShowCompatibilityNotice
  } = useCrossPlatformGatingContext();
  const {
    adActive
  } = useVastAdState();
  const posthog = usePostHog();
  const {
    store
  } = useConfigsContext();
  const {
    handleLogin,
    handleLogout
  } = useAuthContext();
  const {
    controlBarPresentation,
    iframeSettings,
    isEmbeddedWithoutControlBar,
    isFullscreen,
    standaloneUrl
  } = useWorldDetailsContext();
  const {
    roomData,
    setRoomData,
    toggleLikeRoom,
    toggleSaveRoom,
    isPartnerPromise
  } = useCheckEnterRoomContext();
  const sceneSid = 'scene_sid' in roomData.scene ? roomData.scene.scene_sid : undefined;
  const {
    handleAdSdkMessage
  } = useAdSDK({
    iframeRef: ref,
    sceneSid
  });
  const searchParams = useSearchParams();
  const previewMode = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.PREVIEW);
  const token = searchParams.get(IFRAME_SEARCH_PARAM_KEYS.TOKEN);
  const source = searchParams.get(SEARCH_PARAM_KEYS.SOURCE);
  const appId = searchParams.get(SEARCH_PARAM_KEYS.APP_ID);
  const appState = searchParams.get(SEARCH_PARAM_KEYS.APP_STATE);
  const iframeState = searchParams.get(SEARCH_PARAM_KEYS.STATE);
  const htcTokenFromUrl = searchParams.get(SEARCH_PARAM_KEYS.HTC_TOKEN);
  const htcToken = store?.credentials?.htcToken;
  const isFull3D = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D);
  const isVideoDebug = isVideoWorld && searchParams.has(IFRAME_SEARCH_PARAM_KEYS.VIDEO_DEBUG);
  const videoT = searchParams.get('t');
  const videoAutoplay = searchParams.get('autoplay');
  const videoControls = searchParams.get('controls');
  const forceLandscapeIos = searchParams.get(IFRAME_SEARCH_PARAM_KEYS.FORCE_LANDSCAPE_IOS);
  const [iframeUrl, setIframeUrl] = useState(() => {
    const base = buildStandaloneIframeUrl(standaloneUrl, appState, isVideoDebug);
    return appendVideoParams(base, videoT, videoAutoplay, videoControls, forceLandscapeIos);
  });

  // Tear the standalone iframe down when its route becomes hidden so audio stops.
  const {
    iframeSrc: activeIframeSrc,
    setIframeRef
  } = useIframeSrcTeardown(ref, iframeUrl);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isCompatibilityAlertClosed, setIsCompatibilityAlertClosed] = useState(false);
  const shouldShowCompatibilityAlert = shouldShowCompatibilityNotice && !isCompatibilityAlertClosed && !isVideoWorld;
  const [videoSharePanelSource, setVideoSharePanelSource] = useState<'main' | 'embed' | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState<number | undefined>(undefined);
  const shareUrl = buildShareUrl(roomData.hub_sid, {});
  const {
    isCommentPanelOpen,
    toggleCommentPanel,
    closeCommentPanel,
    setPanelHostEl
  } = useVideoCommentsPanelContext();
  const {
    isOpen: isMoreVideosPanelOpen,
    isLoading: isMoreVideoItemsLoading,
    items: moreVideoItems,
    openPanel: openMoreVideosPanel,
    closePanel: closeMoreVideosPanel
  } = useMoreVideosPanelContext();
  const {
    showModal: showFullscreenModal,
    onConfirm
  } = useAutoFullscreen({
    targetId: 'standalone-iframe-wrapper',
    enableModal: !isVideoWorld,
    // With the embedded Control Bar, Exit only leaves fullscreen and must not be re-entered.
    retryAfterExit: controlBarPresentation.kind !== 'embedded'
  });
  const sandboxAttr = getStandaloneIframeSandboxPermissions(iframeSettings).join(' ');
  const allowAttr = getStandaloneIframeAllowPermissions(iframeSettings).join('; ');
  const {
    sendBIReport
  } = useBISdkContext();
  const {
    accountType
  } = useConfigsContext();
  const [viewportSize, setViewportSize] = useState({
    w: 0,
    h: 0
  });
  useEffect(() => {
    const update = () => setViewportSize({
      w: window.innerWidth,
      h: window.innerHeight
    });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const isPortraitFullscreen = isFullscreen && viewportSize.h > viewportSize.w;

  // Portrait dock height = viewport height minus 16:9 player height
  const dockHeightPx = isPortraitFullscreen ? viewportSize.h - Math.min(viewportSize.w * (9 / 16), viewportSize.h - COMMENT_DOCK_MIN_HEIGHT) : 0;
  const heartbeatIndexRef = useRef(1);
  const sessionIdRef = useRef(uuidv4());
  const videoMilestoneIndexRef = useRef(0);
  const currentTimeResolverRef = useRef<((t: number) => void) | null>(null);
  const iframeLoadedRef = useRef(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isLogoRotated, setIsLogoRotated] = useState(false);
  const [parentOrigins, setParentOrigins] = useState<string[]>([]);
  const validatedParentOrigin = useRef<string | null>(null);
  useEffect(() => {
    setIsEmbedded(window.parent !== window);
  }, []);
  useSWR(source && appId && htcTokenFromUrl && source === 'viverse-sdk' ? 'sg-token' : null, () => getSgToken(appId!, htcTokenFromUrl!), {
    onSuccess: data => {
      const base64Response = btoa(JSON.stringify(data));
      const currentUrl = new URL(iframeUrl!);
      currentUrl.searchParams.set('auth_resp', base64Response);
      if (iframeState) {
        currentUrl.searchParams.set('state', iframeState);
      }
      setIframeUrl(currentUrl.toString());
    }
  });
  const handleAddWorldViewCount = async () => {
    if (roomData?.hub_sid) {
      const data = await addWorldViewCount(roomData.hub_sid);
      if (data?.status === 200) {
        const prevViewCount = roomData?.view_count || 0;
        setRoomData(prev => ({
          ...prev,
          view_count: prev.view_count + 1
        }));
        let a15 = '';
        if (roomData.is_voucher_room) {
          a15 = 'nexus';
        } else if (await isPartnerPromise) {
          a15 = 'creator_partnership';
        }
        sendBIReport({
          ev: BI_EVENT_VIEW_COUNT,
          category: BI_CATEGORY_BEHAVIOR_IN_ROOM,
          a1: accountType,
          a2: location.href,
          a3: roomData.title,
          a4: roomData.hub_sid,
          a12: `${prevViewCount + 1}`,
          a13: posthog.get_distinct_id(),
          a14: roomData.vod_id ? 'Video' : 'World',
          a15
        });
      }
    }
  };
  const getHeartbeatIndex = () => {
    return heartbeatIndexRef.current++;
  };
  const getSessionId = () => {
    return sessionIdRef.current;
  };
  const sendToChild = (methods: string, payload: unknown) => {
    if (!ref.current?.contentWindow || !iframeUrl) return;
    try {
      const childOrigin = new URL(iframeUrl).origin;
      ref.current.contentWindow.postMessage({
        methods,
        payload
      }, childOrigin);
    } catch {
      console.warn('Failed to send message to child iframe');
    }
  };
  const sendStatePatch = (payload: {
    like?: {
      active: boolean;
      count?: number;
    };
    bookmark?: {
      active: boolean;
    };
    moreVideosPanel?: {
      active: boolean;
    };
    features?: {
      moreVideos: boolean;
    };
  }) => {
    sendToChild(VIVERSE_VIDEO_METHODS.STATE_PATCH, payload);
  };
  const videoPlayback = useVideoPlaybackContext();

  /**
   * Ask the player iframe for its current playback position.
   * Returns a Promise that resolves with the position in seconds.
   * Times out to 0 after 3 s if the player does not respond.
   */
  const requestCurrentTime = (): Promise<number> => {
    return new Promise(resolve => {
      const timer = setTimeout(() => {
        currentTimeResolverRef.current = null;
        resolve(0);
      }, 3000);
      currentTimeResolverRef.current = (t: number) => {
        clearTimeout(timer);
        resolve(t);
      };
      sendToChild(VIVERSE_VIDEO_METHODS.CURRENT_TIME_REQUEST, {});
    });
  };

  // Keep context fn ref in sync with the latest requestCurrentTime closure (closes over iframeUrl state).

  useEffect(() => {
    videoPlayback?.registerRequestCurrentTime(requestCurrentTime);
  });
  const handleMessage = async (event: MessageEvent) => {
    const isFromChild = event.source === ref.current?.contentWindow;
    if (isFromChild) {
      // Child iframe message — validate via env var (viverse internal domains)
      if (!isValidChildOrigin(event.origin)) {
        console.warn(`Received message from unauthorized child origin: ${event.origin}`);
        return;
      }
      const {
        methods,
        payload
      } = event.data || {};
      const source = event.source!;
      switch (methods) {
        case VIVERSE_BRIDGE_METHODS.C_LOADED:
          {
            source.postMessage({
              methods: VIVERSE_BRIDGE_METHODS.C_LOADED_ACK,
              message: 'iframe window loaded, parent sending handshake ack'
            }, {
              targetOrigin: event.origin
            });
            break;
          }
        case VIVERSE_BRIDGE_METHODS.C_HEARTBEATING:
          {
            const hIndex = getHeartbeatIndex();
            let a15 = '';
            if (roomData.is_voucher_room) {
              a15 = 'nexus';
            } else if (await isPartnerPromise) {
              a15 = 'creator_partnership';
            }
            sendBIReport({
              ev: BI_EVENT_WORLD_ENGAGEMENT,
              category: BI_CATEGORY_USER_ACTIVITY,
              user_agent: navigator.userAgent,
              a1: accountType,
              a2: location.href,
              a3: roomData.title,
              a4: roomData.hub_sid,
              a5: `${hIndex}`,
              a9: getSessionId(),
              a13: posthog.get_distinct_id(),
              a14: roomData.vod_id ? 'Video' : 'World',
              a15,
              a16: null
            });
            break;
          }
        case VIVERSE_BRIDGE_METHODS.C_HEARTBEAT_PAUSE:
          {
            // TODO: user may temp. unavailable to access the iframe content, optional
            //       could remove if we do not address it
            break;
          }
        case VIVERSE_BRIDGE_METHODS.C_HEARTBEAT_RESUME:
          {
            // TODO: user may temp. available to access the iframe content, optional
            //       could remove if we do not address it
            break;
          }
        case VIVERSE_BRIDGE_METHODS.C_UNLOAD:
          {
            // TODO: fired before user close window/tab, optional
            //       could remove if we do not address it
            break;
          }
        case VIVERSE_BRIDGE_METHODS.C_LANDSCAPE_ROTATION:
          {
            const {
              rotated
            } = payload || {};
            setIsLogoRotated(!!rotated);
            break;
          }
      }
      switch (methods) {
        case VIVERSE_SDK_METHODS.CHECK_AUTH:
          {
            const {
              appId
            } = payload || {};
            if (!appId) return;
            if (appId !== sceneSid) {
              source.postMessage({
                methods: 'VIVERSE_SDK/checkAuth:ack',
                payload: {
                  auth_resp: null
                }
              }, {
                targetOrigin: event.origin
              });
              return;
            }
            if (!htcToken) {
              source.postMessage({
                methods: 'VIVERSE_SDK/checkAuth:ack',
                payload: {
                  auth_resp: null
                }
              }, {
                targetOrigin: event.origin
              });
              return;
            }
            try {
              const sgTokenResult = await getSgToken(appId, htcToken);
              source.postMessage({
                methods: 'VIVERSE_SDK/checkAuth:ack',
                payload: {
                  auth_resp: sgTokenResult
                }
              }, {
                targetOrigin: event.origin
              });
            } catch (error) {
              console.error('Failed to get SG token:', error);
              source.postMessage({
                methods: 'VIVERSE_SDK/checkAuth:ack',
                payload: {
                  auth_resp: null
                }
              }, {
                targetOrigin: event.origin
              });
            }
            break;
          }
        case VIVERSE_SDK_METHODS.LOGIN:
          {
            const {
              appId,
              state
            } = payload || {};
            if (!appId || appId !== sceneSid) return;
            try {
              const loginParams: Record<string, string> = {
                [SEARCH_PARAM_KEYS.SOURCE]: 'viverse-sdk',
                [SEARCH_PARAM_KEYS.APP_ID]: appId,
                ...(state && {
                  [SEARCH_PARAM_KEYS.STATE]: state
                }),
                ...(previewMode && {
                  [IFRAME_SEARCH_PARAM_KEYS.PREVIEW]: previewMode
                }),
                ...(token && {
                  [IFRAME_SEARCH_PARAM_KEYS.TOKEN]: token
                })
              };
              handleLogin({
                loginParams
              });
            } catch (error) {
              console.error('Login failed:', error);
            }
            break;
          }
        case VIVERSE_SDK_METHODS.LOGOUT:
          {
            handleLogout();
            break;
          }
      }
      switch (methods) {
        case VIVERSE_APP_METHODS.REQUEST_ADD_POINTS:
          {
            const {
              taskId
            } = payload || {};
            if (!taskId) return;
            const reply = (payload: {
              success: boolean;
              status: string;
            }) => {
              source.postMessage({
                methods: VIVERSE_APP_METHODS.REQUEST_ADD_POINTS_ACK,
                payload
              }, {
                targetOrigin: event.origin
              });
            };
            if (!htcToken) {
              reply({
                success: false,
                status: 'AUTH_ERROR'
              });
              break;
            }
            const response = await completeUserTask(taskId, htcToken);
            if (response?.status === 200) {
              reply({
                success: true,
                status: 'TASK_COMPLETED'
              });
              break;
            }
            if (response?.status === 424) {
              const code = response?.results?.code as number;
              const errorCodeMap = {
                40001: 'TASK_NOT_FOUND',
                40008: 'TASK_UNAVAILABLE'
              } as Record<number, string>;
              reply({
                success: false,
                status: errorCodeMap[code] ?? 'UNKNOWN_ERROR'
              });
              break;
            }
            if (response?.status === 400) {
              reply({
                success: false,
                status: 'AUTH_ERROR'
              });
              break;
            }
            reply({
              success: false,
              status: 'UNKNOWN_ERROR'
            });
            break;
          }
      }
      // Handle VIVERSE_VIDEO/* events from the video player iframe
      if (isVideoWorld) {
        switch (methods) {
          case VIVERSE_VIDEO_METHODS.CONFIG_READY:
            {
              // CONFIG_READY guarantees child's message listener is registered and React has
              // mounted — more reliable than onLoad which waits for all sub-resources.
              setIsIframeLoaded(true);
              // Child's message listener is now registered — safe to send CONFIG_INIT.
              sendToChild(VIVERSE_VIDEO_METHODS.CONFIG_INIT, {
                isEmbed: isEmbedded,
                isMobile: isMobileDevice,
                thumbnailUrl: roomData?.image,
                title: roomData.title,
                creator: {
                  avatarUrl: roomData.owner?.head_icon_url,
                  username: roomData.owner?.display_name
                },
                features: {
                  like: true,
                  bookmark: true,
                  share: true,
                  comment: true,
                  moreVideos: true
                },
                viewerState: {
                  like: {
                    active: roomData.is_liked ?? false,
                    count: roomData.like_count ?? 0
                  },
                  bookmark: {
                    active: roomData.is_saved ?? false
                  }
                },
                ...(isVideoWorld && {
                  adActive
                })
              });
              break;
            }
          case VIVERSE_VIDEO_METHODS.LIKE_CLICK:
            {
              const {
                requestedActive
              } = payload || {};
              if (typeof requestedActive !== 'boolean') break;
              // Only mutate if state differs — prevents double-toggle on desync
              if (requestedActive !== roomData.is_liked) {
                await toggleLikeRoom();
              }
              // statePatch is sent by the useEffect below after roomData updates
              break;
            }
          case VIVERSE_VIDEO_METHODS.SAVE_CLICK:
            {
              const {
                requestedActive
              } = payload || {};
              if (typeof requestedActive !== 'boolean') break;
              // Only mutate if state differs — prevents double-toggle on desync
              if (requestedActive !== roomData.is_saved) {
                await toggleSaveRoom();
              }
              // statePatch is sent by the useEffect below after roomData updates
              break;
            }
          case VIVERSE_VIDEO_METHODS.SHARE_CLICK:
            {
              const {
                playerMode
              } = payload || {};
              if (playerMode === 'embed' || playerMode === 'main') {
                requestCurrentTime().then(t => {
                  setVideoCurrentTime(t);
                  setVideoSharePanelSource(playerMode);
                });
              }
              break;
            }
          case VIVERSE_VIDEO_METHODS.PROFILE_CLICK:
            {
              const communityUserId = roomData.owner.community_user_id;
              if (!communityUserId) {
                console.warn('[PROFILE_CLICK] owner community_user_id is missing, skipping navigation');
                break;
              }
              const profileUrl = `https://${getWorldsDomain()}/profile/${communityUserId}`;
              window.open(profileUrl, '_blank', 'noopener,noreferrer');
              break;
            }
          case VIVERSE_VIDEO_METHODS.TITLE_CLICK:
            {
              const {
                currentTimeSec
              } = payload || {};
              const t = typeof currentTimeSec === 'number' ? currentTimeSec : 0;
              const detailUrl = `${location.origin}${location.pathname}?autoplay=1&t=${Math.floor(t)}`;
              window.open(detailUrl, '_blank', 'noopener,noreferrer');
              break;
            }
          case VIVERSE_VIDEO_METHODS.COMMENT_CLICK:
            {
              toggleCommentPanel();
              break;
            }
          case VIVERSE_VIDEO_METHODS.MORE_VIDEOS_PANEL_REQUEST:
            {
              const {
                requestedActive,
                reason,
                bottomOffsetPx
              } = payload || {};
              if (requestedActive) {
                // Close share and comment panels before showing more videos panel
                setVideoSharePanelSource(null);
                closeCommentPanel();
                openMoreVideosPanel(reason === 'ended' ? 'ended' : 'manual', bottomOffsetPx ?? null);
              } else {
                // Programmatic close (e.g. replay, seek) — does not set dismissedEndedRef
                closeMoreVideosPanel();
              }
              break;
            }
          case VIVERSE_VIDEO_METHODS.FULLSCREEN_REQUEST:
            {
              // Child requests parent to enter/exit fullscreen on the wrapper element.
              // We own the fullscreen state here; the child only expresses intent.
              const {
                requestedActive
              } = payload || {};
              try {
                if (requestedActive) {
                  await enterFullscreen('standalone-iframe-wrapper');
                } else {
                  await exitFullscreen();
                }
              } catch (error) {
                console.warn('[FULLSCREEN_REQUEST] Failed to handle fullscreen request:', error);
              }
              break;
            }
          case VIVERSE_VIDEO_METHODS.CURRENT_TIME_RESPONSE:
            {
              const timeSec = typeof payload?.currentTimeSec === 'number' ? payload.currentTimeSec : 0;
              currentTimeResolverRef.current?.(timeSec);
              currentTimeResolverRef.current = null;
              break;
            }
        }
      }

      // Handle VIVERSE_AD_SDK/* events from the standalone child iframe
      handleAdSdkMessage(event, methods, payload);

      // Child→parent relay: forward non-internal, non-video messages to validated parent.
      // Video events are handled separately below with their own passthrough path.
      const videoEventTypes: readonly string[] = Object.values(VIDEO_EVENT_TYPES);
      const isVideoEvent = isVideoWorld && videoEventTypes.includes((event.data as Record<string, unknown>)?.type as string);
      if (!isInternalMessage(methods) && !isVideoEvent && validatedParentOrigin.current && window.parent !== window) {
        window.parent.postMessage(event.data, validatedParentOrigin.current);
      }
    } else {
      // Parent window message — validate via per-room origin whitelist
      if (!matchOrigin(event.origin, parentOrigins)) {
        console.warn(`Received message from unauthorized parent origin: ${event.origin}`);
        return;
      }

      // Record the validated parent origin for child→parent replies
      validatedParentOrigin.current = event.origin;
      const {
        methods
      } = event.data || {};

      // Block internal namespace messages from parent (security boundary)
      if (isInternalMessage(methods)) {
        console.warn(`Blocked internal namespace message from parent: ${methods}`);
        return;
      }

      // Parent→child relay: forward relay-eligible messages to child iframe
      if (ref.current?.contentWindow && iframeUrl) {
        try {
          const childOrigin = new URL(iframeUrl).origin;
          ref.current.contentWindow.postMessage(event.data, childOrigin);
        } catch {
          console.warn('Failed to relay parent message to child iframe');
        }
      }
    }

    // Handle video player postMessage events (from blendvision iframe)
    if (isVideoWorld) {
      const {
        type
      } = event.data || {};
      const isVideoEvent = type === VIDEO_EVENT_TYPES.VIDEO_STARTED || type === VIDEO_EVENT_TYPES.VIDEO_MILESTONE || type === VIDEO_EVENT_TYPES.VIDEO_PAUSED || type === VIDEO_EVENT_TYPES.VIDEO_RESUMED || type === VIDEO_EVENT_TYPES.VIDEO_ENDED;
      if (isVideoEvent) {
        // Re-post video events to parent window for outer embedding
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(event.data, '*');
        }

        // Send BI report for video events
        let a15 = '';
        if (roomData.is_voucher_room) {
          a15 = 'nexus';
        } else if (await isPartnerPromise) {
          a15 = 'creator_partnership';
        }
        const baseBIPayload = {
          ev: BI_EVENT_VIDEO_WATCH,
          category: BI_CATEGORY_VIDEO_ACTIVITY,
          user_agent: navigator.userAgent,
          a1: accountType,
          a2: location.href,
          a3: roomData.title,
          a4: roomData.hub_sid,
          a9: getSessionId(),
          a13: posthog.get_distinct_id(),
          a14: 'Video',
          a15
        };
        switch (type) {
          case VIDEO_EVENT_TYPES.VIDEO_STARTED:
            {
              videoMilestoneIndexRef.current = 0;
              sendBIReport({
                ...baseBIPayload,
                a5: '0'
              });
              break;
            }
          case VIDEO_EVENT_TYPES.VIDEO_MILESTONE:
            {
              videoMilestoneIndexRef.current++;
              sendBIReport({
                ...baseBIPayload,
                a5: `${videoMilestoneIndexRef.current}`
              });
              break;
            }
          case VIDEO_EVENT_TYPES.VIDEO_PAUSED:
            {
              sendBIReport({
                ...baseBIPayload,
                ev: BI_EVENT_VIDEO_PAUSE,
                a5: `${videoMilestoneIndexRef.current}`
              });
              break;
            }
          case VIDEO_EVENT_TYPES.VIDEO_RESUMED:
            {
              sendBIReport({
                ...baseBIPayload,
                ev: BI_EVENT_VIDEO_RESUME,
                a5: `${videoMilestoneIndexRef.current}`
              });
              break;
            }
          case VIDEO_EVENT_TYPES.VIDEO_ENDED:
            {
              sendBIReport({
                ...baseBIPayload,
                ev: BI_EVENT_VIDEO_END,
                a5: `${videoMilestoneIndexRef.current}`
              });
              break;
            }
        }
      }
    }
  };
  useEventListener('message', handleMessage);
  const handleIframeError = (e: SyntheticEvent<HTMLIFrameElement>) => {
    iframeLoadedRef.current = true;
    console.error('Iframe loading failed:', e);
    window.open(iframeUrl, '_blank');
  };
  const handleIframeLoad = () => {
    iframeLoadedRef.current = true;
    setIsIframeLoaded(true);
  };
  useEffect(() => {
    if (!isVideoWorld) return;
    sendStatePatch({
      like: {
        active: roomData.is_liked ?? false,
        count: roomData.like_count ?? 0
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomData.is_liked, roomData.like_count, isIframeLoaded]);
  useEffect(() => {
    if (!isVideoWorld) return;
    sendStatePatch({
      bookmark: {
        active: roomData.is_saved ?? false
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomData.is_saved, isIframeLoaded]);

  // Sync More Videos panel open/close state to the player iframe.
  // The player uses this to hide the overlay replay button while the panel is visible.
  useEffect(() => {
    if (!isVideoWorld || !isIframeLoaded) return;
    sendStatePatch({
      moreVideosPanel: {
        active: isMoreVideosPanelOpen
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoreVideosPanelOpen]);
  useEffect(() => {
    if (!isVideoWorld || !isIframeLoaded || isMoreVideoItemsLoading) return;
    sendStatePatch({
      features: {
        moreVideos: moreVideoItems.length > 0
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoWorld, isIframeLoaded, isMoreVideoItemsLoading, moreVideoItems.length]);
  useEffect(() => {
    const base = buildStandaloneIframeUrl(standaloneUrl, appState, isVideoDebug);
    setIframeUrl(appendVideoParams(base, videoT, videoAutoplay, videoControls, forceLandscapeIos));
  }, [standaloneUrl, appState, isVideoDebug, videoT, videoAutoplay, videoControls, forceLandscapeIos]);
  useEffect(() => {
    const roomId = roomData?.id;
    validatedParentOrigin.current = null;
    setParentOrigins([]);
    if (!roomId) return;
    let isCancelled = false;
    void getPostMessageOriginsByRoomId(roomId).then(origins => {
      if (isCancelled) return;
      setParentOrigins(origins);

      // Derive parent origin from document.referrer so child→parent relay
      // works immediately, even before the parent sends a message.
      // Falls back gracefully: if referrer is empty (e.g. Referrer-Policy: no-referrer)
      // or doesn't match the whitelist, we wait for an inbound parent message instead.
      if (!validatedParentOrigin.current && document.referrer) {
        try {
          const referrerOrigin = new URL(document.referrer).origin;
          if (matchOrigin(referrerOrigin, origins)) {
            validatedParentOrigin.current = referrerOrigin;
          }
        } catch {
          console.warn('Failed to parse document.referrer as URL');
        }
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [roomData?.id]);
  useTimeout(handleAddWorldViewCount, 3000);
  useEffect(() => {
    if (!isFullscreen) {
      // main share panel requires fullscreen; embed share can remain open outside fullscreen
      setVideoSharePanelSource(prev => prev === 'main' ? null : prev);
      closeCommentPanel();
    }
  }, [isFullscreen, closeCommentPanel]);

  // Notify the video player iframe of the actual fullscreen state after each
  // fullscreenchange event (covers button toggle, ESC key, and browser-initiated exits).
  // Only active for video worlds because only they listen for this message.
  // isFullscreen is provided by useFullscreenStatus() which already subscribes to fullscreenchange.
  useEffect(() => {
    if (!isVideoWorld) return;
    sendToChild(VIVERSE_VIDEO_METHODS.FULLSCREEN_STATE_CHANGED, {
      active: isFullscreen
    });
    // sendToChild reads ref and iframeUrl from closure — both are stable between re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoWorld, isFullscreen]);
  useEffect(() => {
    if (!isVideoWorld) return;
    if (adActive) return;
    sendToChild(VIVERSE_VIDEO_METHODS.AD_STATE_CHANGE, {
      adActive: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoWorld, adActive]);
  const standaloneIframeContent = <>
      <VideoProcessingOverlay />
      <ViverseLogoOverlay roomId={roomData?.hub_sid ?? ''} position={isMobile && isMobileLandscape ? roomData.mobile_landscape_logo_position ?? roomData.logo_overlay_position : isMobile ? roomData.mobile_portrait_logo_position ?? roomData.logo_overlay_position : roomData.logo_overlay_position} isVisible={isIframeLoaded && (isFullscreen || isFull3D) && !roomData.tags?.some(t => (typeof t === 'string' ? t : t.name) === 'Persona') && !isVideoWorld && !roomData.owner.viverse_pro && controlBarPresentation.kind === 'none' &&
    // Embedded content that can have the Control Bar uses it for its logo. Content that never
    // gets the embedded Control Bar (for example Premium) keeps the legacy overlay.
    !isEmbeddedWithoutControlBar} isRotated={isLogoRotated} />
      {isIframeLoaded && showFullscreenModal && <EnterFullscreenModal onConfirm={onConfirm} />}
      {videoSharePanelSource !== null && (isFullscreen || videoSharePanelSource === 'embed') && <SharePanel shareUrl={shareUrl} title={roomData.title} info={roomData} currentTime={videoCurrentTime} onCloseAction={() => setVideoSharePanelSource(null)} />}
      {isVideoWorld && <MoreVideosPanel isMobile={isMobileDevice} />}
      {shouldShowCompatibilityAlert && <RoomAlertOverlay variant="platformCompatibility" onClose={() => setIsCompatibilityAlertClosed(true)} />}
      {activeIframeSrc && <iframe ref={setIframeRef} id="standalone-iframe" title={roomData.title || 'Embedded Content'} className="z-interactive size-full border-none bg-transparent" src={activeIframeSrc} sandbox={sandboxAttr} allow={allowAttr} onError={handleIframeError} onLoad={handleIframeLoad} {...handlers} />}
    </>;
  return <div id="standalone-iframe-wrapper" className={`z-base relative flex h-full w-full ${isPortraitFullscreen ? 'flex-col' : ''}`}>
      <ControlBar presentation={controlBarPresentation}>{standaloneIframeContent}</ControlBar>
      {isFullscreen && (isPortraitFullscreen ?
    // Portrait: dock slides in from bottom with height transition
    <div className="shrink-0 overflow-hidden" style={{
      height: isCommentPanelOpen ? `${dockHeightPx}px` : '0',
      transition: 'height 300ms ease-in-out'
    }}>
            <div className="h-full px-4 pt-2 pb-4">
              <div ref={setPanelHostEl} className="bg-vive-surface-normal h-full w-full overflow-hidden rounded-2xl" />
            </div>
          </div> :
    // Landscape: rail slides in from right with width transition
    <div className="h-full shrink-0 overflow-hidden" style={{
      width: isCommentPanelOpen ? 'max(30vw, 412px)' : '0',
      transition: 'width 300ms ease-in-out'
    }}>
            <div className="flex h-full w-[max(30vw,412px)] py-10 pr-6.5">
              <div ref={setPanelHostEl} className="bg-vive-surface-normal h-full w-full overflow-hidden rounded-2xl" />
            </div>
          </div>)}
    </div>;
}
export default StandaloneIframe;